import React, { useEffect, useRef } from 'react';
import OneSignal from 'onesignal-cordova-plugin';
import { useAuth, useUser } from '@clerk/clerk-react';
import appConfig from '@/utils/config';
import { isFromApp } from '@/biz/tool';

interface Props {}

const canUseOneSignalCordova = () => {
  const globalObj = window as any;
  const hasCordovaRuntime = Boolean(globalObj?.cordova);
  const hasOneSignalApi =
    typeof (OneSignal as any)?.initialize === 'function' &&
    typeof (OneSignal as any)?.Debug?.setLogLevel === 'function' &&
    typeof (OneSignal as any)?.Notifications?.requestPermission === 'function';

  return hasCordovaRuntime && hasOneSignalApi;
};

/** 从 OneSignal 通知 payload 里解析要在壳内 WebView 打开的 https 地址（与 MainActivity 兜底字段对齐） */
function pickHttpsDeepLinkFromNotificationEvent(
  event: unknown
): string | undefined {
  const e = event as {
    notification?: {
      additionalData?: Record<string, unknown>;
      launchURL?: string;
    };
    result?: { url?: string };
  };
  const additionalData = e?.notification?.additionalData ?? {};
  const candidates: unknown[] = [
    additionalData.deepLink,
    additionalData.deep_link,
    additionalData.url,
    additionalData.link,
    e?.notification?.launchURL,
    e?.result?.url
  ];

  for (const raw of candidates) {
    if (typeof raw !== 'string') continue;
    const s = raw.trim();
    if (s.startsWith('https://') || s.startsWith('http://')) {
      return s;
    }
  }
  return undefined;
}

/** Cordova 在 deviceready 之后才保证 exec 可用；若 React 晚于 deviceready 挂载，需兜底再试 */
function whenCordovaReady(run: () => void): () => void {
  const w = window as any;

  let ran = false;
  let pollTimer: number | undefined;
  let fallbackTimer: number | undefined;

  const tryRun = () => {
    if (ran) return;
    if (w?.cordova && typeof w.cordova?.exec === 'function') {
      ran = true;
      run();
    }
  };

  const wrapped = () => {
    tryRun();
  };

  // Cordova 可能在初始化时尚未注入（壳内首屏 origin 不一致常见）。
  // 这里等待 exec 可用后再初始化 OneSignal。
  document.addEventListener('deviceready', wrapped, false);
  tryRun();

  pollTimer = window.setInterval(() => {
    tryRun();
  }, 300);

  // 最长等 12s，避免无限轮询
  fallbackTimer = window.setTimeout(() => {
    document.removeEventListener('deviceready', wrapped, false);
    if (pollTimer) window.clearInterval(pollTimer);
  }, 12000);

  return () => {
    document.removeEventListener('deviceready', wrapped, false);
    if (pollTimer) window.clearInterval(pollTimer);
    if (fallbackTimer) window.clearTimeout(fallbackTimer);
  };
}

const OneSignalConfig: React.FC<Props> = ({}) => {
  const fromApp = isFromApp();
  const { isSignedIn } = useAuth();
  const { user } = useUser();
  const didInit = useRef(false);
  const lastExternalIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!fromApp) return;

    const onNotificationClick = (event: unknown) => {
      const url = pickHttpsDeepLinkFromNotificationEvent(event);
      if (!url) {
        return;
      }
      window.location.assign(url);
    };

    const init = () => {
      if (didInit.current) return;
      if (!canUseOneSignalCordova()) {
        console.warn(
          'OneSignal: window.cordova 不可用，已跳过推送初始化（请确认壳内 WebView 已注入 Cordova，且使用 server.url 直连远程 H5）。'
        );
        return;
      }
      didInit.current = true;
      try {
        OneSignal.Debug.setLogLevel(6);
        OneSignal.initialize(appConfig.oneSignalAppId);
        OneSignal.Notifications.addEventListener('click', onNotificationClick);
        OneSignal.Notifications.requestPermission(false).then(
          (accepted: boolean) => {
            console.log('User accepted notifications: ' + accepted);
          }
        );
      } catch (error) {
        didInit.current = false;
        console.warn('OneSignal initialization failed:', error);
      }
    };

    const cleanupCordova = whenCordovaReady(init);
    return () => {
      cleanupCordova();
      try {
        if (canUseOneSignalCordova()) {
          OneSignal.Notifications.removeEventListener(
            'click',
            onNotificationClick
          );
        }
      } catch {
        // ignore
      }
    };
  }, [fromApp]);

  useEffect(() => {
    if (!fromApp || !didInit.current) return;
    if (!canUseOneSignalCordova()) return;

    try {
      const oneSignalAny = OneSignal as any;
      const canLogin = typeof oneSignalAny?.login === 'function';
      const canLogout = typeof oneSignalAny?.logout === 'function';
      const externalId = user?.id ? String(user.id) : '';

      if (isSignedIn && externalId && canLogin) {
        if (lastExternalIdRef.current !== externalId) {
          oneSignalAny.login(externalId);
          lastExternalIdRef.current = externalId;
        }
        return;
      }

      if (!isSignedIn && canLogout) {
        oneSignalAny.logout();
        lastExternalIdRef.current = null;
      }
    } catch (error) {
      console.warn('OneSignal external ID sync failed:', error);
    }
  }, [fromApp, isSignedIn, user?.id]);

  return null;
};

export default OneSignalConfig;
